import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Terminal, Download, LogOut, Loader2, RefreshCw } from "lucide-react";
import { NewsManager } from "@/components/NewsManager";
import { AdminMonitor } from "@/components/AdminMonitor";

const API = "/api";

const LEARNING_SIM_KEY = "nakedchart_learning_simulation";

function setLearningSimulation(state) {
  const payload = { state, ts: Date.now() };
  localStorage.setItem(LEARNING_SIM_KEY, JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent("nakedchart-learning-simulation", { detail: { state } }));
}

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("atr_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("monitor");

  const fetchList = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/waitlist`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setRows(data.items);
      setError("");
    } catch {
      sessionStorage.removeItem("atr_admin_token");
      setToken("");
      setRows(null);
      setError("Sesi berakhir, silakan masuk kembali.");
    }
  }, []);

  useEffect(() => {
    if (token) fetchList(token);
  }, [token, fetchList]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/admin-login`, { email, password });
      sessionStorage.setItem("atr_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Gagal masuk.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("atr_admin_token");
    setToken("");
    setRows(null);
  };

  const downloadCsv = async () => {
    const { data } = await axios.get(`${API}/admin/waitlist/export`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "waitlist.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="admin-page">
      <header className="border-b border-white/10 bg-ink-surface/60">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-5">
          <a href="/" data-testid="admin-logo" className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center border border-buy/50 bg-buy/10 text-buy">
              <Terminal size={15} />
            </span>
            <span className="font-display text-sm font-bold">
              ATR<span className="text-buy">/V1</span>
              <span className="ml-2 font-mono text-[10px] font-normal uppercase tracking-widest text-mute-dim">admin</span>
            </span>
          </a>
          {token && (
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 border border-white/20 px-3 py-1.5 font-mono text-xs text-mute transition-colors duration-200 hover:border-sell/50 hover:text-sell"
            >
              <LogOut size={13} /> Keluar
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-14">
        {!token ? (
          <motion.form
            onSubmit={login}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto max-w-md border border-white/15 bg-ink-surface p-8"
            data-testid="admin-login-form"
          >
            <h1 className="font-display text-xl font-bold">Masuk Admin</h1>
            <p className="mt-2 font-mono text-xs text-mute-dim">&gt; area terbatas — daftar waitlist</p>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email admin"
              data-testid="admin-email-input"
              className="mt-6 h-11 w-full border border-white/15 bg-ink px-4 font-mono text-sm outline-none transition-colors duration-200 focus:border-buy/60"
            />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="password"
              data-testid="admin-password-input"
              className="mt-3 h-11 w-full border border-white/15 bg-ink px-4 font-mono text-sm outline-none transition-colors duration-200 focus:border-buy/60"
            />
            {error && (
              <p className="mt-3 font-mono text-xs text-sell" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="mt-6 flex h-11 w-full items-center justify-center gap-2 border border-buy bg-buy/10 font-mono text-sm font-bold uppercase tracking-wider text-buy transition-colors duration-200 hover:bg-buy hover:text-ink disabled:opacity-60"
            >
              {loading && <Loader2 size={15} className="animate-spin" />}
              Masuk
            </button>
          </motion.form>
        ) : (
          <div data-testid="admin-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="font-display text-2xl font-bold">
                  {tab === "monitor" ? "Monitoring Sistem" : tab === "kalender" ? "Kalender Berita" : "Daftar Waitlist"}
                </h1>
                {tab === "waitlist" && (
                  <p className="mt-1 font-mono text-xs text-mute-dim" data-testid="admin-count">
                    &gt; total pendaftar: {rows ? rows.length : "..."}
                  </p>
                )}
              </div>
              {tab === "waitlist" && (
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchList(token)}
                    data-testid="admin-refresh-button"
                    className="flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-xs text-mute transition-colors duration-200 hover:text-white"
                  >
                    <RefreshCw size={13} /> Muat Ulang
                  </button>
                  <button
                    onClick={downloadCsv}
                    data-testid="admin-export-button"
                    className="flex items-center gap-2 border border-buy bg-buy/10 px-4 py-2 font-mono text-xs font-bold text-buy transition-colors duration-200 hover:bg-buy hover:text-ink"
                  >
                    <Download size={13} /> Unduh CSV
                  </button>
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-px border border-white/10 bg-white/10" data-testid="admin-tabs">
              {[["monitor", "Monitoring"], ["waitlist", "Waitlist"], ["kalender", "Kalender"], ["simulation", "🤖 Simulation"]].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  data-testid={`admin-tab-${key}`}
                  className={`flex-1 px-4 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest transition-colors duration-200 ${
                    tab === key ? "bg-buy/15 text-buy" : "bg-ink text-mute hover:text-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === "monitor" && <div className="mt-6"><AdminMonitor /></div>}

            {tab === "waitlist" && (
            <div className="mt-8 overflow-x-auto border border-white/10">
              <table className="w-full min-w-[480px] text-left" data-testid="admin-waitlist-table">
                <thead>
                  <tr className="border-b border-white/10 bg-ink-surface">
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-mute-dim">#</th>
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-mute-dim">Email</th>
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-mute-dim">Terdaftar</th>
                  </tr>
                </thead>
                <tbody>
                  {rows === null ? (
                    <tr><td colSpan={3} className="px-4 py-8 text-center font-mono text-xs text-mute-dim">memuat...</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={3} className="px-4 py-8 text-center font-mono text-xs text-mute-dim">belum ada pendaftar</td></tr>
                  ) : (
                    rows.map((r, i) => (
                      <tr key={r.id} className="border-b border-white/5 transition-colors duration-150 hover:bg-white/[0.03]" data-testid={`admin-row-${i}`}>
                        <td className="px-4 py-3 font-mono text-xs text-mute-dim">{rows.length - i}</td>
                        <td className="px-4 py-3 font-mono text-sm text-white">{r.email}</td>
                        <td className="px-4 py-3 font-mono text-xs text-mute">
                          {new Date(r.created_at).toLocaleString("id-ID")}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            )}

                {tab === "simulation" && (
                  <div className="mt-6 border border-white/10 bg-ink-surface/40 p-6">
                    <div className="mb-5">
                      <h2 className="font-display text-lg font-bold text-white">🤖 Learning Robot Simulation</h2>
                      <p className="mt-1 font-mono text-xs text-mute-dim">
                        Mode simulasi admin — hanya mengubah animasi Learning, tidak memengaruhi trading.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {[["idle", "💤", "NO SIGNAL"], ["good", "🟢", "MARKET BAGUS"], ["caution", "🟡", "CAUTION"], ["danger", "🔴", "HIGH RISK"], ["win", "🏆", "WIN"], ["loss", "😵", "LOSS"], ["thinking", "🧠", "LEARNING"], ["avoid", "⛔", "AVOID"]].map(([state, icon, label]) => (
                        <button
                          key={state}
                          type="button"
                          onClick={() => setLearningSimulation(state)}
                          className="flex min-h-[82px] flex-col items-center justify-center gap-1 border border-white/10 bg-black/20 px-3 py-3 font-mono text-xs font-bold text-white transition-all duration-200 hover:border-buy/50 hover:bg-buy/10 active:scale-95"
                        >
                          <span className="text-2xl">{icon}</span>
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={() => setLearningSimulation("idle")}
                      className="mt-4 border border-white/15 px-4 py-2 font-mono text-xs text-mute transition-colors hover:border-white/30 hover:text-white"
                    >
                      Reset Simulation
                    </button>
                  </div>
                )}
            {tab === "kalender" && <NewsManager />}
          </div>
        )}
      </main>
    </div>
  );
}
